/**
 * Shared guard for replaying CACHED preference answers into user_preferences.
 *
 * A replay is a best-effort restore of answers the user gave before they had a
 * session (guest browse-personalize, or the onboarding screens completed before
 * email confirmation). It must never beat an answer that is already stored:
 * the stored value is newer by definition, since the cache was written first.
 *
 * Rule: a cached column applies ONLY where the existing row is null.
 *
 * On the seeded all-null row that handle_new_user() creates at signup, every
 * intended column reads null, so nothing is filtered and the replay payload is
 * identical to an unguarded write. The guard only bites when a real answer
 * already exists.
 *
 * This is NOT used for direct writes from the live screens — a user answering a
 * question right now is authoritative and must be able to change an answer.
 */

/** Drop `undefined` values so a partial cache never sends SQL null. */
function withoutUndefined(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Keep only the payload columns whose value in `existingRow` is null/absent.
 * `user_id` is never filtered — it is the conflict target, not an answer.
 *
 * A null/undefined `existingRow` means "no row found": nothing to preserve, so
 * the payload passes through untouched.
 */
export function filterToNullColumns(
  payload: Record<string, unknown>,
  existingRow: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const cleaned = withoutUndefined(payload);
  if (!existingRow) return cleaned;

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(cleaned)) {
    if (key === "user_id") {
      out[key] = value;
      continue;
    }
    const existing = existingRow[key];
    if (existing === null || existing === undefined) out[key] = value;
  }
  return out;
}

/**
 * True when the filtered payload carries no actual answers (only `user_id`, or
 * nothing at all). Callers skip the upsert entirely in that case.
 */
export function hasNoAnswers(payload: Record<string, unknown>): boolean {
  return Object.keys(payload).filter((k) => k !== "user_id").length === 0;
}
